/**
 * The one list of everything a tag is connected to — sheets, documents and
 * timeseries — behind the Board tab, the Knowledge Graph tab and their counts.
 *
 * It exists because those three surfaces each answered "what is this tag
 * connected to?" from a DIFFERENT subset of what the drill-down had already
 * fetched, so they disagreed with each other and with the Matched table:
 *
 * - the Matched table counts document evidence server-side and shows e.g.
 *   "32 documents, 118 passages";
 * - the Board rendered `sheets` only, so those 32 were nowhere on it;
 * - the Knowledge Graph built nodes from `sheets` + timeseries matches only, so
 *   they were missing there too.
 *
 * A reviewer therefore read "32 connected" on one tab and saw none of them on
 * the next two. Deriving all three from this selector is what makes the counts
 * agree by construction rather than by three call sites staying in step.
 */
import type {
  MatchedTagRow,
  SheetTag,
  TagDocumentRow,
  TagSheetRow,
} from '@/service/api'

/**
 * What kind of artifact a source is. Drives the card a board draws, the node
 * type the graph uses, and where "enlarge" sends the reviewer — a sheet has a
 * drawing to open, a document has pages, a timeseries has neither.
 */
export type ConnectedSourceKind = 'sheet' | 'document' | 'timeseries'

/** One thing the tag under review is connected to. */
export interface ConnectedSource {
  /**
   * Board/graph identity, and the layout key. Namespaced by kind AND carrying
   * the node id, because neither alone is unique:
   *
   * `/tag-sheets` dedupes on (document_id, node_id), so ONE drawing that prints
   * the tag in three places is three rows. The board keyed its layout by
   * `document_id` alone, so those three rows resolved to one position and drew
   * three cards stacked exactly on top of each other — visually one card. That
   * is the second half of "it says 32 connected but the board shows fewer".
   */
  id: string
  kind: ConnectedSourceKind
  /** What the card, the chip and the graph node are labelled with. */
  name: string
  /**
   * The document this source lives in, or null for a timeseries match whose
   * file was deleted. Null means "nothing to preview", not "no source".
   */
  documentId: string | null
  /** Sheets only — the graph editor deep-link target. */
  graphId?: string
  /** Sheets only — the node to highlight once the editor opens. */
  nodeId?: string | null
  /** Sheets only — every device tag on that sheet, for the Node Details chips. */
  sheetTags?: SheetTag[]
  /** Documents only — how many passages in this document name the tag. */
  passageCount?: number
  /** Documents only — the spelling THIS document used, which may differ. */
  mention?: string
  /**
   * 0..1 where the source carries one. Sheets do not: a tag parsed off a
   * drawing is an observation, not a claim about identity, so there is nothing
   * to be confident about and the card shows no badge rather than a fake 100%.
   */
  confidence?: number
  /** Timeseries only — `'manual'` renders as "Manual Approval", not a percent. */
  matchMethod?: string
  /**
   * The file behind the source, when that is not what the card is named after.
   *
   * A timeseries card is labelled with the TAG; the file behind it is the
   * historian export. The preview overlay titles itself from this, because
   * titling it with the tag would claim it is showing something it is not.
   */
  documentName?: string
}

export interface SourceCounts {
  total: number
  sheets: number
  documents: number
  timeseries: number
}

interface Input {
  sheets: TagSheetRow[]
  documents: TagDocumentRow[]
  matches: MatchedTagRow[]
}

/**
 * Collapse a document's passages into ONE source.
 *
 * Putra, in the 2026-09-19 review: "separate the merged documents into
 * individual sources" — one entry per FILE, with its own confidence and its own
 * passage count, rather than the single merged "32 documents, 118 passages"
 * line the Matched table shows. `/tag-documents` returns one row per passage,
 * so thirteen passages in one PDF must not become thirteen cards.
 *
 * Confidence is the MEAN over the document's passages, weighted by how many
 * there are — not the strongest one.
 *
 * The max was tried first and saturates: mention confidence has five discrete
 * values (0.97/0.93 for a registry hit, 0.92/0.80/0.75 for a discovery), so a
 * document with sixty passages needs only ONE spelled exactly as the registry
 * has it to score 0.97. Almost every document has one, so every card read 97%.
 *
 * The card is summarising the whole document, and the mean is what that summary
 * is. A document whose passages are one exact hit and sixty guesses lands near
 * 0.75, which is the truth about it. `_document_mentions` on the server
 * averages the same way, so the board and the Matched table cannot disagree.
 */
const foldDocuments = (documents: TagDocumentRow[]): ConnectedSource[] => {
  const byDocument = new Map<string, ConnectedSource>()
  // Running total per document, so the mean stays exact rather than being
  // folded incrementally into the value it is also read from.
  const totals = new Map<string, number>()
  for (const row of documents) {
    const existing = byDocument.get(row.document_id)
    if (existing) {
      existing.passageCount = (existing.passageCount ?? 0) + 1
      totals.set(row.document_id, (totals.get(row.document_id) ?? 0) + row.confidence)
      existing.confidence = (totals.get(row.document_id) ?? 0) / existing.passageCount
      continue
    }
    totals.set(row.document_id, row.confidence)
    byDocument.set(row.document_id, {
      id: `document:${row.document_id}`,
      kind: 'document',
      name: row.document_name,
      documentId: row.document_id,
      mention: row.mention,
      passageCount: 1,
      confidence: row.confidence,
    })
  }
  return [...byDocument.values()]
}

/**
 * Everything the tag is connected to, in the order the board lays them out:
 * sheets first (the drawing is the thing a reviewer anchors on), then the
 * documents that discuss it, then the timeseries it was matched to.
 */
export const connectedSources = ({ sheets, documents, matches }: Input): ConnectedSource[] => [
  ...sheets.map((sheet, index) => ({
    // `node_id` is nullable, and two rows for one document can BOTH be null —
    // the index keeps them distinct so they still get separate cards.
    id: `sheet:${sheet.document_id}:${sheet.node_id ?? index}`,
    kind: 'sheet' as const,
    name: sheet.document_name,
    documentId: sheet.document_id,
    graphId: sheet.graph_id,
    nodeId: sheet.node_id,
    sheetTags: sheet.sheet_tags,
  })),
  ...foldDocuments(documents),
  ...matches.map((match) => ({
    // `match_id` is already unique per pair; a tag matched to the same
    // timeseries twice cannot happen, and if it did these are genuinely two
    // rows a reviewer has to decide on separately.
    id: `timeseries:${match.match_id}`,
    kind: 'timeseries' as const,
    name: match.tag,
    documentId: match.ts_document_id ?? null,
    documentName: match.ts_document_name ?? undefined,
    confidence: match.confidence,
    matchMethod: match.match_method,
  })),
]

/**
 * The counts the tabs and the empty states read.
 *
 * Derived from the list rather than from the three inputs so a count can never
 * describe something the board is not drawing — which is the whole failure this
 * module exists to prevent. In particular `documents` counts FILES, matching
 * the cards, not the passage rows the API returned.
 */
export const sourceCounts = (sources: ConnectedSource[]): SourceCounts => ({
  total: sources.length,
  sheets: sources.filter((s) => s.kind === 'sheet').length,
  documents: sources.filter((s) => s.kind === 'document').length,
  timeseries: sources.filter((s) => s.kind === 'timeseries').length,
})
